import React, { PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import {
  Colors,
  ColorsType,
  FontSize,
  FontWeight,
  LetterSpacing,
  LetterSpacingTokenType,
  LineHeight,
  Spacing,
} from '@common/tokens';

type Props = PropsWithChildren<{
  id?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  weight?: 'light' | 'medium' | 'bold' | 'bolder';
  color?: ColorsType;
  className?: string;
  transform?: 'capitalize' | 'lowercase' | 'uppercase';
  align?: 'left' | 'center' | 'right';
  italic?: boolean;
  deleted?: boolean;
  secondary?: boolean;
  bold?: boolean; // alias for weight="bold"
  block?: boolean;
  underline?: boolean;
  info?: string;
  dataTestId?: string;
  letterSpacing?: LetterSpacingTokenType;
  /**
   * Holds the text at the top of its scroll container while the rest of its containing block scrolls
   * past, releasing at that block's end. So the caller decides how far it travels by choosing what
   * wraps it, and the nearest scrolling ancestor must be a scroll container for it to pin at all.
   *
   * Forces `display: block`, since a sticky inline box does not hold, and paints a padded
   * background, because content scrolling underneath would otherwise read through the text and
   * arrive flush against it.
   */
  sticky?: boolean;
  /**
   * Background behind the text. Transparent when absent, except under `sticky`, which needs an
   * opaque one and falls back to white. Pass the colour the text actually sits on: a fallback that
   * does not match its surface reads as a white band moving over the page.
   */
  bgColor?: ColorsType;
}>;

export function Text(props: Props) {
  const {
    id,
    size = 'md',
    weight = 'light',
    color = 'textDefault',
    transform,
    align,
    className,
    children,
    italic,
    deleted,
    secondary,
    bold,
    block,
    underline,
    info,
    dataTestId,
    letterSpacing,
    sticky,
    bgColor,
  } = props;

  const finalColor = secondary ? 'midnightLighter' : color;
  const colorHash = Colors[finalColor];
  const backgroundHash = resolveBackground(bgColor, sticky);
  const finalWeight = bold ? 'bold' : weight;
  const internalClassName = clsx(
    className,
    `wk-text wk-text-size--${size} wk-text-weight--${finalWeight}`,
    transform && `wk-text-transform--${transform}`,
    align && `wk-text-align--${align}`,
    letterSpacing && `wk-text-letter-spacing--${letterSpacing}`,
    {
      'wk-text--italic': italic,
      'wk-text--deleted': deleted,
      'wk-text--underline': underline,
      'wk-text--block': block || !!align,
      'wk-text--sticky': sticky,
    }
  );

  return (
    <>
      <span
        id={id}
        title={info}
        data-testid={dataTestId}
        className={internalClassName}
      >
        {applyPropInheritanceInTextChildren(children, props)}
      </span>
      <style jsx>{`
        .wk-text {
          color: ${colorHash};
          background-color: ${backgroundHash};
          margin: 0;
          word-break: break-word;
        }

        .wk-text--italic {
          font-style: italic;
        }

        .wk-text-weight--light {
          font-weight: ${FontWeight.light};
        }
        .wk-text-weight--medium {
          font-weight: ${FontWeight.medium};
        }
        .wk-text-weight--bold {
          font-weight: ${FontWeight.bold};
        }
        .wk-text-weight--bolder {
          font-weight: 800;
        }

        span.wk-text-size--sm {
          font-size: ${FontSize.xs};
          line-height: ${LineHeight.xs};
        }
        span.wk-text-size--md {
          font-size: ${FontSize.sm};
          line-height: ${LineHeight.sm};
        }
        span.wk-text-size--lg {
          font-size: ${FontSize.md};
          line-height: ${LineHeight.md};
        }
        span.wk-text-size--xl {
          font-size: ${FontSize.xl};
          line-height: ${LineHeight.lg};
        }

        .wk-text-transform--capitalize {
          text-transform: capitalize;
        }
        .wk-text-transform--lowercase {
          text-transform: lowercase;
        }
        .wk-text-transform--uppercase {
          text-transform: uppercase;
        }

        .wk-text--block {
          display: block;
        }

        /* After --block so the display wins whether or not the caller also asked for it. */
        .wk-text--sticky {
          position: sticky;
          top: 0;
          z-index: 1;
          display: block;
          /* Part of the background, not spacing the caller chose: the padded band is what the
             text scrolling under a pinned heading passes behind, so without it a line arrives
             flush against the heading and the two read as one. */
          padding: ${Spacing.sm}px 0;
        }

        .wk-text-align--left {
          text-align: left;
        }
        .wk-text-align--center {
          text-align: center;
        }
        .wk-text-align--right {
          text-align: right;
        }

        .wk-text--deleted {
          text-decoration: line-through;
        }

        .wk-text--underline {
          text-decoration: underline;
          text-decoration-color: ${colorHash};
        }

        .wk-text-letter-spacing--xs {
          letter-spacing: ${LetterSpacing.xs};
        }
        .wk-text-letter-spacing--sm {
          letter-spacing: ${LetterSpacing.sm};
        }
        .wk-text-letter-spacing--md {
          letter-spacing: ${LetterSpacing.md};
        }
        .wk-text-letter-spacing--lg {
          letter-spacing: ${LetterSpacing.lg};
        }
      `}</style>
    </>
  );
}

/**
 * `sticky` needs an opaque background to be legible over what scrolls beneath it, so it takes white
 * when the caller named no colour. Everything else stays transparent, which is what text on a page
 * has always been.
 */
function resolveBackground(
  bgColor: ColorsType | undefined,
  sticky: boolean | undefined
): string {
  if (bgColor) return Colors[bgColor];

  return sticky ? Colors.white : 'transparent';
}

function applyPropInheritanceInTextChildren(
  children: ReactNode,
  parentTextProps: Props
): ReactNode {
  return React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Text) {
      // Typography inherits; the parent's own box does not. A nested Text pinning itself, or
      // repainting the parent's background over its own line, is never what the caller asked for.
      const {
        children: _,
        dataTestId: __,
        className: ___,
        id: ____,
        sticky: _____,
        bgColor: ______,
        ...parentInheritable
      } = parentTextProps;

      return React.cloneElement(child, {
        ...parentInheritable,
        ...child.props,
      });
    }
    return child;
  });
}
